import { Box, Button, Flex, Stack, Text, Tooltip, useDisclosure } from '@chakra-ui/react'
import { PopUpMessageContext, ScreenContext, useApp, useAppRole } from '@hb/react'
import { generatePaymentSchedule } from '@hb/shared'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useResizeObserver } from '../../../../hooks'
import { useUpdateDoc } from '../../../../hooks/backend/useUpdateDoc'
import { ActionButton, SolidActionButton } from '../../../Buttons/ActionButton'
import LoadingIcon from '../../../LoadingIcon'
import { DefaultModal } from '../../../Modals/DefaultModal'
import { SelectAssessmentBox } from '../Assessments/SelectAssessmentBox'
import { AssessmentDocuments } from '../Documents/AssessmentDocuments'
import { DraftView } from '../Documents/DraftView'
import { AddPaymentPopover } from './AddPaymentPopover'
import { PaymentsDue } from './PaymentsDue'
import { PaymentsLog } from './PaymentsLog'
import { PaymentsContext } from './PaymentsProvider'
import { ReceivedPayments } from './ReceivedPayments/ReceivedPayments'

export const NoPayments = ({ mobileLayout }: { mobileLayout: boolean }) => {
  const { modal } = useContext(PaymentsContext)
  const {
    user,
    assessmentId,
    selectedAssessment: { populated: selectedAssessment, adminRef },
  } = useProfile()
  const { processResponse } = useContext(PopUpMessageContext)
  const [isLoading, setIsLoading] = useState(false)
  const { payments } = selectedAssessment ?? {}
  const { appName } = useApp()
  const update = useUpdateDoc()
  const generate = useCallback(async () => {
    if (payments?.due) {
      return processResponse({ error: 'Patient already has payments' })
    }
    if (!selectedAssessment || !user || !assessmentId)
      return processResponse({ error: 'no assessment' })
    setIsLoading(true)
    const res = await update(
      adminRef,
      'payments',
      generatePaymentSchedule(user, assessmentId, selectedAssessment),
    )
    setIsLoading(false)
    return processResponse(res)
  }, [user, adminRef, payments, processResponse, selectedAssessment, update, assessmentId])
  return (
    <Flex
      bg="white"
      borderRadius={6}
      boxShadow="1px 1px 3px #00000066"
      direction="column"
      py={2}
      px={3}>
      <Flex flexFlow={mobileLayout ? 'column' : 'row'} gap={mobileLayout ? 0 : 1.5} w="100%">
        <Text fontSize="md">No payments scheduled for this pregnancy</Text>
        {appName === 'providers-app' ? (
          <Box ml={mobileLayout ? 0 : 'auto'} mt={mobileLayout ? 1 : 0}>
            <AddPaymentPopover
              buttonSize="xs"
              buttonText="ADD RECEIVED PAYMENT"
              placement="left-start"
            />
          </Box>
        ) : null}
      </Flex>
      {appName === 'app' ? (
        <Flex mt={1}>
          <SolidActionButton isLoading={isLoading} onClick={generate} size="sm">
            Generate Payment Schedule
          </SolidActionButton>
          <ActionButton bg="white" size="sm" ml={2} onClick={modal.onOpen}>
            Manual Entry
          </ActionButton>
        </Flex>
      ) : null}
    </Flex>
  )
}

export const AssessmentPayments = ({ preview }: { preview?: boolean }) => {
  const {
    selectedAssessment: { populated: selectedAssessment },
  } = useProfile()
  const { appName } = useApp()
  const appRole = useAppRole()
  const { payments } = selectedAssessment ?? {}

  const bodyRef = useRef<HTMLDivElement>(null)
  const { width } = useResizeObserver(bodyRef, 'content')
  const mobileLayout = useMemo(() => width < 400, [width])
  return (
    <Flex ref={bodyRef} w="100%" flexFlow="column">
      {payments && Object.keys(payments).length ? (
        <>
          <PaymentsDue mobileLayout={mobileLayout} />
          {(appName === 'app' || appRole === 'super-admin' || appRole === 'lead-admin') &&
          !preview ? (
            <ReceivedPayments width={width} mobileLayout={mobileLayout} />
          ) : null}
        </>
      ) : (
        <NoPayments mobileLayout={mobileLayout} />
      )}
    </Flex>
  )
}

export const AssessmentPaymentsTab = () => {
  const {
    selectedAssessment: { populated: selectedAssessment },
    loading,
    isInvite,
    assessmentId,
    user,
  } = useProfile()
  const patientId = user?.id
  const { appName } = useApp()
  const { midwife, drafts } = selectedAssessment ?? {}

  if (!midwife && loading) return <LoadingIcon />
  return (
    <Box px={2} w="100%">
      <SelectAssessmentBox overlay>
        <Stack pt={4} direction="column" spacing={3}>
          {/* <PaymentsNote /> */}
          <PaymentsLog />
          <AssessmentPayments />
          {appName === 'app' ? (
            <DraftView
              name="Receipts/Invoices Template"
              draft={drafts?.invoicesReceiptsTemplates}
              templateKey="invoiceAndReceipt"
            />
          ) : null}
          {assessmentId && patientId ? (
            <AssessmentDocuments
              adminView
              isInvite={isInvite}
              patientId={patientId}
              category="invoiceAndReceipt"
              assessmentId={assessmentId}
            />
          ) : null}
        </Stack>
      </SelectAssessmentBox>
    </Box>
  )
}

const PaymentsPopUpTrigger = ({ onOpen }: { onOpen: () => void }) => (
  <Tooltip placement="top" label="Patient Payments">
    <Button
      onClick={onOpen}
      bg="green.600"
      color="white"
      position="absolute"
      left={2}
      top={2}
      size="xs">
      $
    </Button>
  </Tooltip>
)

export const PaymentsPopUp = () => {
  const { width: screenWidth } = useContext(ScreenContext)
  const { isOpen, onClose, onOpen } = useDisclosure()
  const width = useMemo(() => Math.min(screenWidth * 0.95, 600), [screenWidth])
  return (
    <>
      <PaymentsPopUpTrigger onOpen={onOpen} />
      <DefaultModal
        size="2xl"
        overlayHeader
        isOpen={isOpen}
        contentProps={{ p: 0, width: `${width}px` }}
        bodyProps={{ p: 2 }}
        onClose={onClose}
        render={() => <AssessmentPaymentsTab />}
      />
    </>
  )
}
